import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";

import { Button, Checkbox, Divider, Dropdown, Icon, Input, Loader } from "semantic-ui-react";
import Cookies from "js-cookie";

// APIs
import ProductServiceKindListView from "issara-sdk/apis/ProductServiceKindListView_core";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";

// Interface
import { MasterOptionsType } from "./sequence/PackagePurchase";
import { PackageItemDetailType } from "./sequence/PackageDateTime";
import { USAGE_LIMIT_TYPES, UsageLimitType } from "./sequence/SettingPackage";
import { getValidPackageSelection, hasValidPackageQuantity } from "./sequence/packageSelection";
import { useIntl } from "react-intl";

// Types
type CardAppointmentPackageProps = {
  setProp: (key: string, value: any, callback?: () => any) => any;
  // data
  data: DataType[];
  apiToken?: string | null;
  division?: number | string | null;
  headerName?: string;
  languageUX?: string;
  hideHistory?: boolean; // * ซ่อนปุ่ม history;
  isCancel?: boolean; // * mode ยกเลิก package;
  isEdit?: boolean; // * mode แก้ไข;
  isPackageBinder?: boolean; // * mode หัวข้อ;

  // config
  selectable?: boolean; // * select product ได้;
  showQty?: boolean;
  usageLimit?: UsageLimitType;
  // CommonInterface
  buttonLoadCheck?: Record<string, any>;
  // options
  masterOptions?: MasterOptionsType;
  // callback
  onCancel?: (reason: string, callback?: () => any) => any;
  onClose?: () => any;
  onApprove?: (data: any[], check: number[], card: string) => any;
  onHistory?: (data: Partial<HistoryDataType>) => any;
  onSelect?: (data: Record<string, any>[]) => any;
  onSelectionStateChange?: (state: PackageSelectionState) => void;
  // labels (optional overrides)
  selectPromptText?: string;
  confirmButtonText?: string;
};

export type PackageSelectionState = {
  isReady: boolean;
  selectedCount: number;
};

type HistoryDataType = {
  product_code: string;
  product_id: number;
  product_name: string;
};

export type ServiceKindType = {
  kind: string;
  label: string;
  enabled: boolean;
};

const DEFAULT_SERVICE_KINDS: ServiceKindType[] = [
  { kind: "LAB", label: "Lab", enabled: true },
  { kind: "IMAGING", label: "Imaging", enabled: true },
  { kind: "TREATMENT", label: "Treatment", enabled: true },
  { kind: "SUPPLY", label: "Supply", enabled: true },
  { kind: "DRUG", label: "Drug", enabled: true },
  { kind: "MISCELLANEOUS", label: "Miscellaneous", enabled: true },
  { kind: "DOCTOR_FEE", label: "Doctor Fee", enabled: true },
  { kind: "ROOM_RATE", label: "Room Rate", enabled: true },
  { kind: "NURSE_RATE", label: "Nurse Rate", enabled: true },
  { kind: "FOOD_TYPE", label: "Food Type", enabled: true },
];

type DataType = Pick<
  PackageItemDetailType,
  | "active"
  | "balance"
  | "p_type_code"
  | "product"
  | "product_code"
  | "product_name"
  | "quantity_appointment"
> & {
  id?: number;
  package_order?: number;
  package?: number;
  package_group_code?: string;
  product_package_group_code?: string | null;
  kind?: string;
  product_name_en?: string;
  is_giveaway?: boolean;
  is_free?: boolean;
  is_gift?: boolean;
  giveaway?: boolean;
  price?: string | number;
  unit_price?: string | number;
};

type ItemType = {
  balance?: number | string | null;
  code?: string;
  count?: number;
  disabled?: boolean;
  header?: boolean;
  original?: DataType;
  qty?: number | string;
  table?: boolean;
  title: string;
  type?: string;
};

const BUTTON_ACTIONS = {
  cancel: "CANCEL_PACKAGE",
};

const GridCenter = { display: "grid", placeContent: "center" } as CSSProperties;
const QuantityHeaderText: CSSProperties = {
  fontFamily: "inherit",
  fontSize: "1rem",
  fontWeight: 400,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
};

const hasAvailableBalance = (balance: number | string | null | undefined): boolean =>
  Number.isFinite(Number(balance)) && Number(balance) > 0;

// Package group codes use shorter names for these service kinds.
const PACKAGE_GROUP_SERVICE_KINDS: Record<string, string> = {
  ROOM: "ROOM_RATE",
  NURSE: "NURSE_RATE",
  FOOD: "FOOD_TYPE",
};

const getServiceKind = (item: DataType): string => {
  const code = (
    item.product_package_group_code ||
    item.p_type_code ||
    item.package_group_code ||
    item.kind ||
    ""
  ).toUpperCase();

  return PACKAGE_GROUP_SERVICE_KINDS[code] || code;
};

export const CARD_APPOINTMENT_PACKAGE = "CardAppointmentPackage";

const CardAppointmentPackage = React.forwardRef((props: CardAppointmentPackageProps, ref: any) => {
  const intl = useIntl();
  const [serviceKinds, setServiceKinds] = useState<ServiceKindType[]>(DEFAULT_SERVICE_KINDS);
  const [isLoadingServiceKinds, setIsLoadingServiceKinds] = useState(true);
  // Checked
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [items, setItems] = useState<ItemType[]>([]);

  const [checkableByType, setCheckableByType] = useState<
    Record<string, { header: number; items: number[] }>
  >({});
  const [initializedCheckable, setInitializedCheckable] = useState<typeof checkableByType | null>(null);
  const [preparedInput, setPreparedInput] = useState<{
    data: DataType[];
    kinds: ServiceKindType[];
    apiToken: string | null | undefined;
    division: number | string | null | undefined;
    isHideQty: boolean | undefined;
    otherGroupTitle: string;
  } | null>(null);

  const [cancelReason, setCancelReason] = useState<Partial<{
    open: boolean;
    reason: any;
  }> | null>(null);

  // options
  const [cancelPackageOrderOptions, setCancelPackageOrderOptions] = useState<
    MasterOptionsType["cancelPackageOrder"]
  >([]);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingServiceKinds(true);
    setServiceKinds(DEFAULT_SERVICE_KINDS);

    const apiToken = props.apiToken || Cookies.get("apiToken");
    const division = props.division || Cookies.get("division_id") || Cookies.get("divisionId");

    ProductServiceKindListView.get({
      params: { pagination_class: "page_number" },
      apiToken,
      extra: { division },
    })
      .then(([res]: any) => {
        if (isMounted && Array.isArray(res)) {
          setServiceKinds(res.filter((kind: ServiceKindType) => kind.enabled === true));
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (isMounted) setIsLoadingServiceKinds(false);
      });

    return () => {
      isMounted = false;
    };
  }, [props.apiToken, props.division]);

  useImperativeHandle(ref, () => ({
    items,
    checkedProductIds: getCheckedProductIds(),
    isReady: isSelectionReady,
  }));

  // Memo Effect
  const isHideQty = useMemo(
    () =>
      // เป็น encounter เดียว และ เป็นการเลือกจากหน้า Select Package
      props.usageLimit === USAGE_LIMIT_TYPES.ONLY_ONCE_IN_ENCOUNTER && props.selectable,
    [props.selectable, props.usageLimit],
  );

  const packageCount = useMemo(() => {
    if (!props.data || props.data.length === 0) {
      return 0;
    }
    const uniquePackages = new Set(
      props.data
        .map((item: any) => item.package_order || item.package)
        .filter(Boolean),
    );
    return uniquePackages.size || 1;
  }, [props.data]);

  const selectedProductCount = useMemo(
    () =>
      items.filter((item, index) => !item.header && index !== 0 && checkedIds.includes(index))
        .length,
    [items, checkedIds],
  );

  const totalProductCount = useMemo(
    () => items.filter((item, index) => !item.header && index !== 0).length,
    [items],
  );

  const checkableAll = useMemo(
    () =>
      Object.values(checkableByType).flatMap((item) => [
        ...(item.header !== undefined ? [item.header] : []),
        ...item.items,
      ]),
    [checkableByType],
  );

  const otherGroupTitle = intl.formatMessage({ id: "อื่น ๆ" });
  const isSelectionReady =
    !isLoadingServiceKinds &&
    preparedInput?.data === props.data &&
    preparedInput.kinds === serviceKinds &&
    preparedInput.apiToken === props.apiToken &&
    preparedInput.division === props.division &&
    preparedInput.isHideQty === isHideQty &&
    preparedInput.otherGroupTitle === otherGroupTitle &&
    initializedCheckable === checkableByType;

  // Effect
  useEffect(() => {
    if (isLoadingServiceKinds) return;

    // group by type {[type]: any[]}
    const group = {} as Record<string, DataType[]>;

    for (const item of props.data) {
      const typeCode = getServiceKind(item);

      group[typeCode] ??= [];
      group[typeCode].push(item);
    }

    // Keep enabled kinds in API order, then group items without an enabled kind.
    const apiKindKeys = Array.from(new Set(serviceKinds.map((kind) => kind.kind.toUpperCase())));
    const orderedGroups = apiKindKeys
      .filter((key) => group[key]?.length > 0)
      .map((key) => ({ key, title: key, items: group[key] }));

    const otherItems = props.data.filter((item) => {
      const typeCode = getServiceKind(item);
      return !apiKindKeys.includes(typeCode);
    });

    if (otherItems.length > 0) {
      orderedGroups.push({
        key: "__UNMATCHED_SERVICE_KIND__",
        title: otherGroupTitle,
        items: otherItems,
      });
    }

    const flatList: ItemType[] = orderedGroups.flatMap(({ key, title: groupTitle, items: value }) => {
      return [
        {
          count: value.length,
          disabled: value.every((item) => !hasAvailableBalance(item.balance)),
          title: groupTitle,
          type: key,
          header: true,
        },
        ...value.map((item) => {
          let quantity: number | string = "";

          if (hasAvailableBalance(item.balance)) {
            const balance = Number(item.balance);
            if (item.quantity_appointment !== undefined && item.quantity_appointment !== null) {
              const requested = Number(item.quantity_appointment);
              quantity = Number.isFinite(requested) ? Math.min(Math.max(requested, 0), balance) : 0;
            } else {
              quantity = isHideQty ? balance : Math.min(1, balance);
            }
          } else {
            quantity = 0;
          }

          const title = item.product_name_en
            ? `${item.product_name_en} (${item.product_name || ""})`
            : item.product_name || "";

          return {
            balance: item.balance,
            code: item.product_code,
            original: item,
            qty: quantity,
            title,
            type: key,
          };
        }),
      ];
    });

    const list = [{ balance: "จำนวนคงเหลือ", code: "Code", title: "รายการ" }, ...flatList];

    setItems(list);
    handleGetCheckableByType(list);
    setPreparedInput({
      data: props.data,
      kinds: serviceKinds,
      apiToken: props.apiToken,
      division: props.division,
      isHideQty,
      otherGroupTitle,
    });
  }, [isHideQty, isLoadingServiceKinds, otherGroupTitle, props.data, props.apiToken, props.division, serviceKinds]);

  useEffect(() => {
    // default checkedids เมื่อเป็นการ edit เท่านั้น
    if (!props.isEdit) {
      return;
    }

    const activeIndex = items.flatMap((item, index) =>
      item.original?.active && hasAvailableBalance(item.balance) && hasAvailableBalance(item.qty)
        ? [index]
        : [],
    );
    // check header เมื่อเลือก children ทั้งหมด
    const headerIndex = Object.values(checkableByType).flatMap((item) =>
      item.items.every((acc) => activeIndex.includes(acc)) ? [item.header] : [],
    );

    setCheckedIds([...activeIndex, ...headerIndex]);
    setInitializedCheckable(checkableByType);
  }, [checkableByType, props.isEdit]);

  useEffect(() => {
    setCancelPackageOrderOptions(props.masterOptions?.cancelPackageOrder || []);
  }, [props.masterOptions?.cancelPackageOrder]);

  // Default select all checkboxes
  useEffect(() => {
    if (!props.isEdit) {
      if (props.selectable && checkableAll.length > 0) {
        handleCheckedAll(null, { checked: true });
      }
      setInitializedCheckable(checkableByType);
    }
  }, [checkableAll, checkableByType, props.isEdit, props.selectable]);

  // Callback
  const handleGetCheckableByType = useCallback((list: ItemType[]) => {
    // group index ที่สามารถ check ได้
    const checkableByType: Record<string, { header: number; items: number[] }> = {};

    for (const [index, item] of list.entries()) {
      if (index === 0) {
        continue;
      } // ข้ามรายการแรก

      const isCheckable = (hasAvailableBalance(item.balance) || item.header) && !item.disabled;
      const type = item.type || "";

      if (isCheckable) {
        if (type in checkableByType) {
          checkableByType[type].items.push(index);
        } else {
          checkableByType[type] = { items: [], header: index };
        }
      }
    }

    setCheckableByType(checkableByType);
  }, []);

  const handleChange = useCallback(
    (index: number, item: ItemType) => (e: any, data: any) => {
      let value = Number(data.value);

      const balance = Number(item.balance);

      // type number value ไม่น้อยกว่า 0 และ น้อยกว่าเท่ากับ balance
      if (value < 0) {
        value = 0;
      } else if (value > balance) {
        value = balance;
      }

      (items as any)[index][data.name] = value;

      setItems([...items]);

      if (data.name === "qty" && value === 0) {
        const headerIndex = item.type ? checkableByType[item.type]?.header : undefined;
        setCheckedIds((current) =>
          current.filter((id) => id !== index && id !== headerIndex),
        );
      }
    },
    [checkableByType, items],
  );

  const getCheckedProductIds = useCallback(() => {
    const productIndexes = Object.values(checkableByType).flatMap((item) => item.items);

    return checkedIds.filter(
      (id) => productIndexes.includes(id) && hasValidPackageQuantity(items[id]),
    );
  }, [checkableByType, checkedIds, items]);

  const checkedProductIds = getCheckedProductIds();
  const selectedValidCount = getValidPackageSelection(items, checkedProductIds).length;

  useEffect(() => {
    props.onSelectionStateChange?.({
      isReady: isSelectionReady,
      selectedCount: selectedValidCount,
    });
  }, [isSelectionReady, selectedValidCount, props.onSelectionStateChange]);

  // Memo
  const isCheckedAll = useMemo(
    () =>
      checkableAll.length > 0 &&
      checkableAll.every((id) => checkedIds.includes(id)),
    [checkableAll, checkedIds],
  );

  const minWidthStyle = useMemo(() => {
    if (isHideQty) {
      return "50rem";
    } else if (props.selectable) {
      return "53rem";
    }

    return "";
  }, [isHideQty, props.selectable]);

  // Handler
  const restoreSelectedQuantities = (indexes: number[]) => {
    setItems((current) => {
      let changed = false;
      const updated = current.map((item, index) => {
        if (
          !indexes.includes(index) ||
          item.header ||
          !hasAvailableBalance(item.balance) ||
          hasAvailableBalance(item.qty)
        ) {
          return item;
        }
        changed = true;
        return { ...item, qty: isHideQty ? Number(item.balance) : Math.min(1, Number(item.balance)) };
      });
      return changed ? updated : current;
    });
  };

  const handleCheckedAll = (e: any, data: any) => {
    if (data.checked) restoreSelectedQuantities(checkableAll);
    setCheckedIds(data.checked ? [...checkableAll] : []);
  };

  const getChildrenIndexes = (items: any[], data: any) =>
    items.flatMap((item, index) =>
      item.type === data.type && !item.header && hasAvailableBalance(item.balance) && !item.disabled
        ? [index]
        : [],
    );

  const getHeaderIndex = (items: any[], data: any) =>
    items.findIndex((item) => item.type === data.type && item.header);

  const updateCheckedIds = (
    checked: number[],
    indexList: number[],
    headerIndex: number,
    isChecked: boolean,
  ) => {
    if (isChecked) {
      return Array.from(new Set([...checked, ...indexList]));
    } else {
      return checked.filter((index) => !indexList.includes(index));
    }
  };

  const handleChecked = (index: number) => (e: any, v: any) => {
    let checked = [...checkedIds];

    const data = items[index];
    if (!data) return;

    const childrenIndex = getChildrenIndexes(items, data);
    const headerIndex = getHeaderIndex(items, data);
    const indexList = [headerIndex, ...childrenIndex];

    if (data.header) {
      checked = updateCheckedIds(checked, indexList, headerIndex, v.checked);
    } else if (v.checked) {
      checked = Array.from(new Set([...checked, index]));
    } else {
      checked = checked.filter((cIndex) => index !== cIndex);
    }

    const groupHeader = items.find((it) => it.header && it.type === data.type);

    if (groupHeader) {
      const headerIdx = items.indexOf(groupHeader);
      const sameTypeChildren = getChildrenIndexes(items, data);
      const checkedChildrenCount = checked.filter((idx) => sameTypeChildren.includes(idx)).length;

      // ถ้า child ถูกเช็กครบ → header ของ type ต้องเช็ก
      if (
        checkedChildrenCount === sameTypeChildren.length &&
        sameTypeChildren.length > 0 &&
        !checked.includes(headerIdx)
      ) {
        checked.push(headerIdx);
      }
      // ถ้ามี child ถูก uncheck อย่างน้อย 1 → header ของ type ต้อง uncheck
      else if (checkedChildrenCount < sameTypeChildren.length && checked.includes(headerIdx)) {
        checked = checked.filter((idx) => idx !== headerIdx);
      }
    }

    if (v.checked) restoreSelectedQuantities(data.header ? childrenIndex : [index]);
    setCheckedIds(checked);
  };

  const handleAppointment = () => {
    const activeItems = props.data.map((item) => {
      const index = items.findIndex((acc) => acc.original?.product_code === item.product_code);

      return {
        ...item,
        active: !!checkedIds.includes(index) && !!Number(items[index].qty),
        quantity_appointment: items[index].qty,
      };
    });

    props.onSelect?.(activeItems);
  };

  const handleCancelPackage = () => {
    setCancelReason({ open: true });
  };

  const handleChangeReason = (e: any, v: any) => {
    setCancelReason({ ...cancelReason, reason: v.value });
  };

  const handleCloseModCancel = () => {
    setCancelReason(null);
  };

  const handleConfirmCancel = () => {
    props.onCancel?.(cancelReason?.reason, () => {
      // callback success
      handleCloseModCancel();
    });
  };

  const handleAddition = (e: any, v: any) => {
    setCancelPackageOrderOptions([
      { key: v.value, text: v.value, value: v.value },
      ...(cancelPackageOrderOptions || []),
    ]);
  };

  return (
    <div className="appointment-package-container" style={{ minWidth: minWidthStyle }}>
      {!!props.headerName && (
        <>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
              padding: "18px 10px 3px 25px",
            }}
          >
            <div
              style={{
                color: "#0072bc",
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              {props.headerName}
            </div>
            <Icon color="red" name="close" size="large" link onClick={props.onClose} />
          </div>
          <Divider style={{ margin: "1rem 1rem 0" }} />
        </>
      )}
      <div style={{ padding: "1.5rem 2rem 0" }} className="appointment-package-title">
        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem 1rem",
            justifyContent: "space-between",
            marginBottom: "0.75rem",
          }}
        >
          <div style={{ fontWeight: "bold" }}>
            {props.isPackageBinder
              ? props.selectPromptText || "กรุณาเลือกรายการที่ต้องการส่งคำสั่งแพทย์"
              : "รายการที่สามารถนัดหมายได้"}
          </div>
          <div style={{ color: "#8A959C", fontSize: "0.95rem", marginLeft: "auto", whiteSpace: "nowrap" }}>
            <span>{packageCount} แพ็กเกจ</span>
            <span style={{ margin: "0 6px" }}>·</span>
            <span>
              เลือก <strong style={{ color: "#0072bc" }}>{selectedProductCount}</strong>/
              {totalProductCount} รายการ
            </span>
          </div>
        </div>

        {isLoadingServiceKinds ? (
          <>
            <Loader active inline="centered" />
            {props.onClose && (
              <div style={{ display: "flex", justifyContent: "center", margin: "1rem" }}>
                <Button color="grey" onClick={props.onClose}>
                  ปิดหน้าต่าง
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <ItemList
              checkedIds={checkedIds}
              hideHistory={props.hideHistory}
              isCheckedAll={isCheckedAll}
              isHideQty={isHideQty}
              isPackageBinder={props.isPackageBinder}
              items={items}
              selectable={props.selectable}
              showQty={props.showQty}
              onChecked={handleChecked}
              onCheckedAll={handleCheckedAll}
              onHistory={props.onHistory}
              onInputChange={handleChange}
            />

            {props.isPackageBinder ? (
              <div
                style={{ display: "flex", justifyContent: "center", margin: "10px" }}
                className="package-binder-button"
              >
                <ButtonLoadCheck
                  // function
                  setProp={props.setProp}
                  // config
                  color={"green"}
                  name={"ORDER_PACKAGE_BINDER"}
                  // data
                  disabled={!isSelectionReady || checkedProductIds.length === 0}
                  paramKey={`${CARD_APPOINTMENT_PACKAGE}_ORDER_PACKAGE_BINDER`}
                  title={props.confirmButtonText || "ยืนยันคำสั่งแพทย์"}
                  buttonLoadCheck={
                    props.buttonLoadCheck?.[`${CARD_APPOINTMENT_PACKAGE}_ORDER_PACKAGE_BINDER`]
                  }
                  onClick={() =>
                    props.onApprove?.(items, getCheckedProductIds(), CARD_APPOINTMENT_PACKAGE)
                  }
                />
                <Button color={"grey"} style={{ marginRight: "1rem" }} onClick={props.onClose}>
                  ปิดหน้าต่าง
                </Button>
              </div>
            ) : (
              <div style={{ display: "flex", margin: "1rem -0.5rem" }}>
                <div style={{ flex: 1 }} />
                {props.selectable && (
                  <Button
                    color={props.isEdit ? "yellow" : "green"}
                    disabled={checkedIds.length === 0}
                    style={{ marginRight: "1rem" }}
                    onClick={handleAppointment}
                  >
                    {props.isEdit ? "แก้ไขรายการนัดหมาย" : "เพิ่มรายการรอลงนัดหมาย"}
                  </Button>
                )}

                {props.isCancel && (
                  <Button color={"red"} style={{ marginRight: "1rem" }} onClick={handleCancelPackage}>
                    ยกเลิก Package
                  </Button>
                )}

                {!props.headerName && (
                  <Button color={"blue"} style={{ marginRight: "1rem" }} onClick={props.onClose}>
                    ปิดหน้าต่าง
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ModConfirm
        denyButtonColor="red"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        openModal={!!cancelReason?.open}
        size="mini"
        titleColor="blue"
        titleName={intl.formatMessage({ id: "กรุณาระบุเหตุผลการยกเลิก Package" })}
        onCloseWithDimmerClick={handleCloseModCancel}
        onDeny={handleCloseModCancel}
        approveButton={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            // config
            color={"green"}
            name={BUTTON_ACTIONS.cancel}
            // data
            paramKey={`${CARD_APPOINTMENT_PACKAGE}_${BUTTON_ACTIONS.cancel}`}
            size="medium"
            title={intl.formatMessage({ id: "ยืนยัน" })}
            basic
            buttonLoadCheck={
              props.buttonLoadCheck?.[`${CARD_APPOINTMENT_PACKAGE}_${BUTTON_ACTIONS.cancel}`]
            }
            onClick={handleConfirmCancel}
          />
        }
        content={
          <div style={{ margin: "-2rem 0px -1rem" }}>
            <div style={{ textAlign: "left" }}>
              <div style={{ margin: "20px 0px 10px 0px" }}>
                {intl.formatMessage({ id: "สาเหตุ" })}
              </div>

              <Dropdown
                value={cancelReason?.reason || ""}
                allowAdditions
                fluid
                search
                selection
                options={cancelPackageOrderOptions}
                onAddItem={handleAddition}
                // callback
                onChange={handleChangeReason}
              />
            </div>
          </div>
        }
      />
    </div>
  );
});

/* ------------------------------------------------------ */

/*                        ListItem                        */

/* ------------------------------------------------------ */
type ItemListProps = {
  isPackageBinder?: boolean;
  checkedIds: number[];
  hideHistory?: boolean;
  isCheckedAll: boolean;
  isHideQty?: boolean;
  items: ItemType[];
  selectable?: boolean;
  showQty?: boolean;
  onChecked: (index: number) => (e: any, v: any) => void;
  onCheckedAll: (e: any, data: any) => void;
  onHistory?: (data: Partial<HistoryDataType>) => any;
  onInputChange: (index: number, item: ItemType) => (e: any, data: any) => void;
};

const ItemList = (props: ItemListProps) => {
  const compactBinderColumns =
    props.isPackageBinder && props.hideHistory && props.showQty && !props.isHideQty;
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!compactBinderColumns || !list) {
      setScrollbarWidth(0);
      return;
    }

    const measureScrollbar = () => setScrollbarWidth(list.offsetWidth - list.clientWidth);
    measureScrollbar();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measureScrollbar);
    observer.observe(list);
    return () => observer.disconnect();
  }, [compactBinderColumns, props.items.length]);

  const handleInputRef = useCallback(
    (isActive: boolean) => (ref: any) => {
      if (!ref?.inputRef?.current) return;

      const inputElement = ref.inputRef.current;
      if (isActive) {
        inputElement.style.border = "none";
        inputElement.style.opacity = "0.75";
        inputElement.style.background = "#EAEAEA";
      } else {
        inputElement.style.border = "";
        inputElement.style.opacity = "";
        inputElement.style.background = "";
      }
      inputElement.style.textAlign = compactBinderColumns ? "center" : "right";
      inputElement.style.paddingLeft = compactBinderColumns ? "0.35em" : "";
      inputElement.style.paddingRight = compactBinderColumns ? "0.35em" : "";
    },
    [compactBinderColumns],
  );

  const getItemStyle = (item: ItemType, index: number): CSSProperties => {
    const isGiveaway =
      !item.header &&
      index !== 0 &&
      Boolean(
        item.original?.is_giveaway ||
          item.original?.is_gift ||
          item.original?.is_free ||
          item.original?.giveaway,
      );
    const isPinkContent =
      isGiveaway || Boolean(item.original && ["DRUG", "TREATMENT"].includes(getServiceKind(item.original)));

    return {
      backgroundColor: item.header && props.isPackageBinder ? "#F3F8FC" : isGiveaway ? "#FFF5F7" : "",
      borderBottom:
        item.header && props.isPackageBinder
          ? "1px solid #DBEAF4"
          : props.items.length - 1 === index ? "1px dashed #CCCCCC" : "",
      borderLeft: isGiveaway ? "3px solid #EE5C7E" : "",
      borderTop: item.header
        ? props.isPackageBinder ? "1px solid #DBEAF4" : "1px dashed #CCCCCC"
        : "",
      display: "grid",
      fontWeight: "normal",
      gap: "20px",
      ...(index === 0 && {
        backgroundColor: "#d0eef2",
        border: "1px solid #979797",
        position: "sticky",
        top: 0,
        zIndex: 2,
      }),
      ...(props.showQty
        ? {
            gridTemplateColumns: "15% minmax(0, 1fr) minmax(10rem, 15%) minmax(10rem, 15%) 15%",
            padding: item.header ? "7px 25px 7px 5px" : "5px 25px 5px 5px",
          }
        : {
            gridTemplateColumns: "15% minmax(0, 1fr) minmax(10rem, 15%) 15%",
            padding: "5px 25px",
          }),
      ...(props.hideHistory && {
        gridTemplateColumns: "minmax(0, 1fr) minmax(10rem, 15%) minmax(10rem, 15%) 2.5%",
      }),
      ...(props.isHideQty && {
        gridTemplateColumns: "minmax(0, 1fr) minmax(10rem, 15%) 2.5%",
      }),
      ...(compactBinderColumns && {
        alignItems: "center",
        backgroundColor: index === 0 ? "#FCECF1" : item.header ? "#FFFFFF" : isPinkContent ? "#FFF5F7" : "#E8F6F8",
        border: index === 0 ? "1px solid #F3C9D5" : "none",
        borderTop: item.header && index > 1 ? "1px solid #E1E8EC" : undefined,
        borderBottom: index === props.items.length - 1 ? "1px solid #E1E8EC" : undefined,
        boxShadow: index === 0 || item.header
          ? undefined
          : isPinkContent ? "inset 3px 0 #EE5C7E" : "inset 3px 0 #3AB0C3",
        gap: "0.5rem",
        gridTemplateColumns: "minmax(0, 1fr) 7rem 7.25rem",
        padding: index === 0
          ? `0.65rem calc(0.75rem + ${scrollbarWidth}px - 1px) 0.65rem calc(0.75rem - 1px)`
          : item.header ? "1rem 0.75rem 0.5rem" : "0.75rem",
      }),
    };
  };

  const renderItemTitle = (item: ItemType) => {
    const isGiveaway = Boolean(
      item.original?.is_giveaway ||
        item.original?.is_gift ||
        item.original?.is_free ||
        item.original?.giveaway,
    );

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          ...(props.isPackageBinder && item.original && { fontSize: "0.95rem", lineHeight: 1.35 }),
        }}
      >
        <span>{item.title}</span>
        {isGiveaway && (
          <div style={{ marginTop: "4px" }}>
            <span
              style={{
                alignItems: "center",
                backgroundColor: "#FFEBF0",
                border: "1px solid #FFCDD8",
                borderRadius: "12px",
                color: "#E91E63",
                display: "inline-flex",
                fontSize: "0.75rem",
                gap: "4px",
                padding: "1px 8px",
              }}
            >
              <Icon name="gift" style={{ margin: 0, fontSize: "0.85rem" }} /> ของแถม
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderCode = (item: ItemType, index: number) => (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        minWidth: 0,
        ...(props.isPackageBinder && !compactBinderColumns && index !== 0 && !item.header && {
          paddingLeft: "1.25rem",
        }),
        ...((item.header || (compactBinderColumns && index === 0)) && {
          fontSize: "1.05rem",
          fontWeight: "bold",
        }),
      }}
    >
      {props.selectable && (
        <Checkbox
          aria-label={item.header ? item.title : undefined}
          checked={index === 0 ? props.isCheckedAll : props.checkedIds.includes(index)}
          disabled={
            index !== 0 && (item.header ? item.disabled : !hasAvailableBalance(item.balance) || item.disabled)
          }
          style={{ flexShrink: 0, marginRight: "1rem" }}
          onChange={index === 0 ? props.onCheckedAll : props.onChecked(index)}
        />
      )}
      {item.header ? (
        <div style={{ alignItems: "baseline", display: "flex", gap: "6px" }}>
          <span style={{ color: "#2185d0", fontSize: "1.05rem", fontWeight: "bold" }}>
            {item.title}
          </span>
          {item.count !== undefined && (
            <span style={{ color: "#8A959C", fontSize: "0.85rem", fontWeight: "normal" }}>
              ({item.count} รายการ)
            </span>
          )}
        </div>
      ) : props.isPackageBinder ? (
        renderItemTitle(item)
      ) : (
        item.code
      )}
    </div>
  );

  const renderTitle = (item: ItemType) => (
    <div style={{ display: "flex" }}>{item.header ? "" : renderItemTitle(item)}</div>
  );

  const renderQuantity = (item: ItemType, index: number) =>
    props.showQty &&
    !props.isHideQty && (
      <div
        style={{
          ...GridCenter,
          ...(index === 0 && QuantityHeaderText),
          ...(compactBinderColumns && {
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
            ...(index === 0 && { fontWeight: 700 }),
          }),
        }}
      >
        {index === 0
          ? "จำนวนนัดหมาย"
          : !item.header && (
              <Input
                ref={handleInputRef(!hasAvailableBalance(item.balance))}
                disabled={!hasAvailableBalance(item.balance)}
                name="qty"
                style={{ opacity: 1, width: compactBinderColumns ? "5.5rem" : "100px", minWidth: 0 }}
                type="number"
                value={item.qty}
                fluid
                onChange={props.onInputChange(index, item)}
              />
            )}
      </div>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      {props.items.length > 0 && (
        <div key={`header`} className="header-item-list" style={getItemStyle(props.items[0], 0)}>
          {renderCode(props.items[0], 0)}
          {!props.isPackageBinder && renderTitle(props.items[0])}
          <div
            style={{
              ...GridCenter,
              ...QuantityHeaderText,
              ...(compactBinderColumns && { fontWeight: 700 }),
            }}
          >
            {props.items[0].balance}
          </div>
          {renderQuantity(props.items[0], 0)}
          {!props.hideHistory && (
            <div style={GridCenter}>
              <RenderRightColumn
                data={props.items[0]}
                hideHistory={props.hideHistory}
                index={0}
                onHistory={props.onHistory}
              />
            </div>
          )}
        </div>
      )}

      {/* Data */}
      <div
        ref={listRef}
        style={{
          flex: 1,
          overflow: "auto",
          maxHeight: "60vh",
        }}
      >
        {props.items.slice(1).map((item, index) => (
          <div key={`item-${item.title}-${index + 1}`} style={getItemStyle(item, index + 1)}>
            {renderCode(item, index + 1)}
            {!props.isPackageBinder && renderTitle(item)}
            <div style={GridCenter}>{item.balance}</div>
            {renderQuantity(item, index + 1)}
            {!props.hideHistory && (
              <div style={GridCenter}>
                <RenderRightColumn
                  data={item}
                  hideHistory={props.hideHistory}
                  index={index + 1}
                  onHistory={props.onHistory}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------ */

/*                    RenderRightColumn                   */

/* ------------------------------------------------------ */
type RenderRightColumnProps = {
  data: Record<string, any>;
  hideHistory?: boolean;
  index: number;
  onHistory?: (data: Record<string, any>) => void;
};

const RenderRightColumn = (props: RenderRightColumnProps) => {
  if (props.hideHistory) {
    return null;
  } else if (props.index === 0) {
    return <>ประวัติ</>;
  } else if (!props.data.header) {
    return (
      <Button
        color="grey"
        icon="history"
        size="mini"
        style={{ fontSize: "0.75rem" }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();

          props.onHistory?.({
            product_code: props.data.original?.product_code,
            product_id: props.data.original?.product,
            product_name: props.data.original?.product_name,
          });
        }}
      />
    );
  }

  return null;
};

const MemoizedCardAppointmentPackage = React.memo(CardAppointmentPackage);
MemoizedCardAppointmentPackage.displayName = "CardAppointmentPackage";
export default MemoizedCardAppointmentPackage;
